import type { MenuProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { PT, fieldSx } from './portalUi';

// A MUI <Select>'s dropdown menu renders in a portal at <body>, so NO parent sx
// reaches it — it falls back to MUI's light default even when the field itself
// is dark. Pass this as the Select's `MenuProps` to skin the open menu in the
// portal's dark-teal language. Pair with `fieldSx` on the Select for the closed
// control. Fixes every country/state and other MUI Select at once.
//
// Consumes the existing portalUi tokens only — does NOT touch the design system.
export const selectMenuProps: Partial<MenuProps> = {
  // Anchor the menu cleanly below the field (modern dropdown), not overlapping it.
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: {
    sx: {
      mt: 0.5,
      maxHeight: 320,
      backgroundColor: PT.surface,
      backgroundImage: 'none',
      color: PT.text,
      border: `1px solid ${PT.borderStrong}`,
      borderRadius: '10px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
      '& .MuiMenuItem-root': {
        color: PT.text,
        fontSize: 14,
        '&:hover': { backgroundColor: 'rgba(56,232,255,0.10)' },
        '&.Mui-selected': {
          backgroundColor: 'rgba(56,232,255,0.16)',
          '&:hover': { backgroundColor: 'rgba(56,232,255,0.22)' }
        }
      },
      '& .MuiList-root': { backgroundColor: 'transparent' }
    }
  }
};

// The closed <Select> control: the shared dark input look plus the dropdown
// arrow + value text in the portal palette. Use as the Select's `sx`, paired
// with `MenuProps={selectMenuProps}` for the open menu.
export const selectSx: SxProps<Theme> = {
  ...(fieldSx as object),
  '& .MuiSelect-icon': { color: PT.textMuted },
  '& .MuiSelect-select': { color: PT.text }
};

// Re-exported for locality.
export { fieldSx };
